import { useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Check, Copy, Info } from 'lucide-react';

import CallScoreBadge from '@/components/call/call-score-badge';
import { ApiSchemas } from '@/components/providers/v1-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import { MetricRowCell } from './metric-row-cell';

type ExternalCallListOut = ApiSchemas['ExternalCallListOut'];

export function createMetricColumn(key: string, label: string): ColumnDef<ExternalCallListOut> {
  return {
    accessorKey: 'metrics_' + key,
    header: label,
    cell: ({ row }) => {
      return <MetricRowCell metric={row.original?.metrics?.[key]} />;
    },
  };
}

export const CALL_COLUMNS: ColumnDef<ExternalCallListOut>[] = [
  {
    accessorKey: 'user_name',
    header: 'CSR',
    cell: ({ row }) => <div className='font-medium'>{row.original.user_name ?? '—'}</div>,
  },
  {
    accessorKey: 'caller_phone_number',
    header: 'Phone Number',
    cell: ({ row }) => <PhoneNumberCell row={row} />,
  },
  {
    accessorKey: 'start_time',
    header: 'Date',
    cell: ({ row }) => {
      const startTimestamp = row.original.start_time!;
      const startDate = new Date(startTimestamp);
      return <div>{format(startDate, 'MMM d, hh:mm a')}</div>;
    },
  },
  {
    accessorKey: 'duration_ms',
    header: 'Duration',
    cell: ({ row }) => {
      const duration = (row.original.duration_ms ?? 0) / 1000;
      return <div>{duration.toFixed(0)}s</div>;
    },
  },
  {
    accessorKey: 'team',
    header: 'Team',
    cell: ({ row }) => <div>{row.original.team ?? '—'}</div>,
  },
  {
    accessorKey: 'ring_groups',
    header: 'Group',
    cell: ({ row }) => <div>{row.original.ring_groups ?? '—'}</div>,
  },
  {
    accessorKey: 'call_type',
    header: 'Direction',
    cell: ({ row }) => <div>{row.original.call_type ?? '—'}</div>,
  },
  {
    accessorKey: 'classified_call_type',
    header: 'Category',
    cell: ({ row }) => {
      const classifiedType = row.original.classified_call_type;
      const metrics_status = row.original.metrics_status;

      // If we have classified_call_type, display it as a badge
      if (classifiedType) {
        // Extract prefix and content
        const trimmedType = classifiedType.startsWith('conversation.')
          ? classifiedType.replace('conversation.', '').trim()
          : classifiedType;

        // Determine badge variant based on prefix
        const isConversation = classifiedType.startsWith('conversation:');
        const isVoicemail = classifiedType.startsWith('voicemail:');

        let variant: 'default' | 'secondary' | 'outline' = 'secondary';
        if (isConversation) {
          variant = 'default';
        } else if (isVoicemail) {
          variant = 'outline';
        } else {
          variant = 'secondary';
        }

        return <Badge variant={variant}>{trimmedType}</Badge>;
      }

      // If no classified_call_type but we have metrics_status, show processing/skipped with tooltip
      if (metrics_status === 'pending') {
        return (
          <div className='flex items-center gap-1'>
            <Badge variant='outline' className='border-blue-500 text-blue-500'>
              processing
            </Badge>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className='h-3 w-3 text-muted-foreground' />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Call categorization is being processed and appears in timeline</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      }

      if (metrics_status === 'skipped') {
        return (
          <div className='flex items-center gap-1'>
            <Badge variant='outline' className='text-muted-foreground'>
              Skipped
            </Badge>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className='h-3 w-3 text-muted-foreground' />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Call evaluation was skipped</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      }

      return <div>—</div>;
    },
  },
  {
    accessorKey: 'avg_score',
    header: 'Metrics Score',
    cell: ({ row }) => {
      const avg_score = row.original.metrics_avg_score;
      if (avg_score === undefined || avg_score === null) {
        return <div>—</div>;
      }
      return <CallScoreBadge score={avg_score} />;
    },
  },
  {
    accessorKey: 'booking_status',
    header: 'Booking Status',
    cell: ({ row }) => {
      const booking_status = row.original.booking_status;
      switch (booking_status) {
        case 'eligible_booked':
          return <Badge variant='default'>Eligible (booked)</Badge>;
        case 'eligible_not_booked':
          return <Badge variant='secondary'>Eligible (not-booked)</Badge>;
        case 'excluded':
          return <Badge variant='outline'>Excluded</Badge>;
        default:
          return <div>—</div>;
      }
    },
  },

  {
    accessorKey: 'resolution_status',
    header: 'Resolution Status',
    cell: ({ row }) => {
      const classified_resolved_status = row.original.classified_resolved_status;
      switch (classified_resolved_status) {
        case 'resolved':
          return <Badge variant='outline'>Resolved</Badge>;
        case 'unresolved':
          return <Badge variant='outline'>Unresolved</Badge>;
        case 'partially_resolved':
          return <Badge variant='outline'>Partially Resolved</Badge>;
        default:
          return <div>{classified_resolved_status ? classified_resolved_status : '—'}</div>;
      }
    },
  },
];

function PhoneNumberCell({ row }: { row: { original: ExternalCallListOut } }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await navigator.clipboard.writeText(row.original.caller_phone_number || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  return (
    <div className='flex items-center justify-start px-1'>
      <div className='rounded p-1 font-medium'>{row.original.caller_phone_number}</div>
      <Button
        variant='ghost'
        size='sm'
        onClick={handleCopy}
        className='ml-2 h-auto p-1'
        title={copied ? 'Copied!' : 'Copy phone number'}
      >
        {copied ? (
          <Check className='h-4 w-4 text-muted-foreground' />
        ) : (
          <Copy className='h-4 w-4 text-muted-foreground' />
        )}
      </Button>
    </div>
  );
}
